/**
 * AddApiKeyModal — Modal dialog for adding or replacing a BYOK API key.
 *
 * AC-1514 (epic AC-1493 Phase 7A). Rendered by `ApiKeysSettingsPage` via
 * `ProviderKeyCard`'s "Add key" / "Replace" action.
 *
 * ## Behavior
 *
 *   - Single plaintext input for the raw API key (masked by default, with a
 *     show/hide toggle on the right). Optional display-label field below
 *     for user-friendly identification.
 *   - "Test and Save" button. While the mutation is pending, the button
 *     shows a spinner and the form is disabled — this captures the backend
 *     contract (the POST validates the key against the provider before
 *     persisting; on 400 the key is not stored).
 *   - On success, closes the modal and surfaces a toast in the parent
 *     (the parent owns the toast; the modal just reports outcome via
 *     `onSuccess`).
 *   - On failure, surfaces the server's error message inline (4xx body
 *     contains a `detail` string per the backend convention) and keeps
 *     the modal open so the user can correct and retry. The key is NOT
 *     persisted on validation failure.
 *
 * ## Why one component for add AND replace
 *
 * The two flows are interaction-identical from the user's perspective —
 * the only difference is the underlying mutation (POST vs PUT) and the
 * label on the button. Centralizing keeps validation, masking, and
 * error-rendering in one place. The parent passes `mode="add"` or
 * `mode="replace"` to pick the mutation; the form contents are the same.
 */

import React, { useState } from 'react';
import { AlertTriangle, Eye, EyeOff, Loader2 } from 'lucide-react';
import Modal from '../common/Modal';
import { LLMProvider } from '../../types';
import {
  useCreateApiKey,
  useUpdateApiKey,
} from '../../hooks/queries/useApiKeyMutations';

export interface AddApiKeyModalProps {
  open: boolean;
  provider: LLMProvider;
  providerLabel: string;
  /** "add" creates via POST, "replace" overwrites via PUT. */
  mode: 'add' | 'replace';
  /** Existing display label (when replacing). */
  initialDisplayLabel?: string | null;
  onClose: () => void;
  /** Invoked after a successful save. The page handles toasts and refresh. */
  onSuccess?: () => void;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { detail?: string } }; message?: string };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  if (typeof err?.message === 'string' && err.message.length > 0) return err.message;
  return fallback;
}

const AddApiKeyModal: React.FC<AddApiKeyModalProps> = ({
  open,
  provider,
  providerLabel,
  mode,
  initialDisplayLabel,
  onClose,
  onSuccess,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [displayLabel, setDisplayLabel] = useState(initialDisplayLabel ?? '');
  const [showKey, setShowKey] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useCreateApiKey();
  const updateMutation = useUpdateApiKey();
  const activeMutation = mode === 'add' ? createMutation : updateMutation;
  const isPending = activeMutation.isPending;

  // Reset state whenever the modal opens fresh for a new provider/mode.
  React.useEffect(() => {
    if (open) {
      setApiKey('');
      setDisplayLabel(initialDisplayLabel ?? '');
      setShowKey(false);
      setErrorMessage(null);
    }
  }, [open, provider, mode, initialDisplayLabel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) {
      setErrorMessage('API key is required.');
      return;
    }
    setErrorMessage(null);
    try {
      await activeMutation.mutateAsync({
        provider,
        data: {
          api_key: apiKey.trim(),
          display_label: displayLabel.trim() || null,
        },
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setErrorMessage(
        extractErrorMessage(
          err,
          'Failed to validate the key. Please verify it and try again.',
        ),
      );
    }
  };

  const title = mode === 'add'
    ? `Add ${providerLabel} API key`
    : `Replace ${providerLabel} API key`;

  return (
    <Modal isOpen={open} onClose={isPending ? () => undefined : onClose}>
      <form onSubmit={handleSubmit} className="p-6" data-testid="add-api-key-modal">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-1">
          {title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          The key is validated against the provider before being saved. It is
          stored encrypted and never returned again — keep a copy if needed.
        </p>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="api-key-input"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              API key *
            </label>
            <div className="relative">
              <input
                id="api-key-input"
                data-testid="api-key-input"
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={isPending}
                autoComplete="off"
                spellCheck={false}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                placeholder={
                  provider === 'openai'
                    ? 'sk-...'
                    : provider === 'anthropic'
                      ? 'sk-ant-...'
                      : 'API key'
                }
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                disabled={isPending}
                aria-label={showKey ? 'Hide key' : 'Show key'}
                className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
              >
                {showKey ? (
                  <EyeOff className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <Eye className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="display-label-input"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Display label (optional)
            </label>
            <input
              id="display-label-input"
              data-testid="display-label-input"
              type="text"
              value={displayLabel}
              onChange={(e) => setDisplayLabel(e.target.value)}
              disabled={isPending}
              maxLength={64}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              placeholder="e.g. Personal account"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              A friendly name shown on the settings card. Does not affect
              authentication.
            </p>
          </div>

          {errorMessage && (
            <div
              role="alert"
              data-testid="api-key-error"
              className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start"
            >
              <AlertTriangle
                className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5 mr-2"
                aria-hidden="true"
              />
              <p className="text-sm text-red-700 dark:text-red-300">
                {errorMessage}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending || !apiKey.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
            )}
            {isPending
              ? 'Validating...'
              : mode === 'add'
                ? 'Test and Save'
                : 'Test and Replace'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddApiKeyModal;
